import {
  useLazyQuery,
  useSubscription,
  useSuspenseQuery,
} from '@apollo/client';
import { datadogLogs } from '@datadog/browser-logs';
import { RumInitConfiguration, datadogRum } from '@datadog/browser-rum';
import * as Sentry from '@sentry/react';
import { useEffect, useMemo } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useMount } from 'react-use';

import { FCC } from '@eluve/components';
import {
  LoggerArgs,
  LoggingContextProvider,
  useExtendedLogger,
} from '@eluve/logger';
import { usePostHog } from '@eluve/posthog-react';
import { useUserIdFromSession } from '@eluve/session-helpers';
import {
  getCoreTenantDataQuery,
  listenTenantFeatureFlagsSubscription,
} from '@eluve/shared-gql-operations';
import { useEluveExtExists, useExternalEhrs } from '@eluve/smart-blocks';
import {
  DbProvider,
  DeviceIdProvider,
  UserFileSystemInitializer,
  UserFileSystemReloadGuard,
  UserFileSystemStoreProvider,
  useDeviceId,
} from '@eluve/user-local-files';
import { useTenantLocalStorage } from '@eluve/utility-hooks';

import { appConfig } from '../../../../config';
import { initPostMessenger } from '../../utils/post-messenger';

const CORE_TENANT_POLL_INTERVAL = 1000 * 60 * 15; // 15 minutes

export const TenantProvider: FCC = ({ children }) => {
  const { tenantId } = useParams() as { tenantId: string };

  const tenantLoggerArgs: LoggerArgs = useMemo(
    () => ({ name: 'TenantScoped', metadata: { tenantId } }),
    [tenantId],
  );
  const tenantLogger = useExtendedLogger(tenantLoggerArgs);

  const userId = useUserIdFromSession();
  const externalEhrs = useExternalEhrs(tenantId);
  const { eluveExtExists } = useEluveExtExists();

  const tenantData = useSuspenseQuery(getCoreTenantDataQuery, {
    variables: {
      tenantId,
    },
  });

  const [executePoll, pollingQuery] = useLazyQuery(getCoreTenantDataQuery, {
    // Serve the first one from cache only since we already retrieved this
    // data using suspense above. After that polling uses the default fetch policy.
    fetchPolicy: 'cache-only',
    nextFetchPolicy: 'cache-first',
  });

  useMount(() => {
    executePoll({ variables: { tenantId } });
    pollingQuery.startPolling(CORE_TENANT_POLL_INTERVAL);
  });

  useSubscription(listenTenantFeatureFlagsSubscription, {
    variables: {
      tenantId: tenantId,
    },
  });

  useEffect(() => {
    if (
      eluveExtExists &&
      externalEhrs?.length &&
      tenantData?.data?.tenantsByPk
    ) {
      initPostMessenger(
        externalEhrs.map(({ domain, vendor }) => ({
          domain,
          vendor,
        })),
        tenantId,
        tenantData.data.tenantsByPk.name,
      );
    }
  }, [eluveExtExists, externalEhrs, tenantData, tenantId]);

  const [advancedMonitoring] = useTenantLocalStorage({
    tenantId,
    key: 'ADVANCED_MONITORING',
    initialValue: false,
  });

  useEffect(
    function updateDatadogRumUserDetails() {
      (async () => {
        if (userId && tenantId) {
          let trackingConsent: RumInitConfiguration['trackingConsent'] =
            'not-granted';
          const isStaging =
            appConfig.VITE_METRICS_ENV.includes('staging') ||
            appConfig.VITE_METRICS_ENV.includes('eluve-sargis');
          if (isStaging) {
            trackingConsent = 'granted';
          } else {
            trackingConsent = advancedMonitoring ? 'granted' : 'not-granted';
          }

          datadogRum.setTrackingConsent(trackingConsent);
          datadogRum.setUser({
            id: userId,
            tenantId,
          });
        }
      })();
    },
    [userId, tenantId, advancedMonitoring],
  );

  if (tenantData.data.tenantsByPk?.isPendingApproval) {
    return <Navigate to={`/organizations/pending-approval`} />;
  }

  return (
    <UserFileSystemStoreProvider
      initializer={() => ({
        userId,
        tenantId,
        userFileSystem: {
          type: 'pending',
        },
      })}
    >
      <LoggingContextProvider loggerInput={tenantLogger}>
        <DbProvider userId={userId} tenantId={tenantId}>
          <DeviceIdProvider userId={userId} tenantId={tenantId}>
            <DeviceIdObservabilityTagger />
            <UserFileSystemReloadGuard />
            <UserFileSystemInitializer>{children}</UserFileSystemInitializer>
          </DeviceIdProvider>
        </DbProvider>
      </LoggingContextProvider>
    </UserFileSystemStoreProvider>
  );
};

/**
 * Reads the resolved device ID from context and tags it onto every
 * observability surface — Sentry, PostHog, Datadog browser logs — so
 * stranded-row incidents can be correlated to specific devices. Runs once
 * per (userId, tenantId) mount; subsequent renders no-op via the effect deps. [ENG-4180]
 */
const DeviceIdObservabilityTagger = () => {
  const deviceId = useDeviceId();
  const postHog = usePostHog();

  useEffect(() => {
    Sentry.setTag('device_id', deviceId);
    postHog.register({ device_id: deviceId });
    datadogLogs.setGlobalContextProperty('device_id', deviceId);

    // Clear on unmount or device-id change so a tenant/account switch doesn't
    // leak the previous device's tag onto telemetry emitted before the next
    // resolver completes.
    return () => {
      Sentry.setTag('device_id', undefined);
      postHog.unregister('device_id');
      datadogLogs.removeGlobalContextProperty('device_id');
    };
  }, [deviceId, postHog]);

  return null;
};
